import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { MoreVertical } from "lucide-react";

const MENU_WIDTH = 192;
const GAP = 4;

// actions: [{ key, label, icon: LucideIcon, onClick, danger, disabled, disabledTitle }]
// O menu vai num portal no <body> com position fixed: dentro da tabela ele era cortado pelo
// overflow do wrapper (que existe pro scroll horizontal). Abre pra cima quando falta espaço
// embaixo e fecha ao rolar ou redimensionar, já que a posição é calculada na abertura.
export const RowActionsMenu = ({ actions }) => {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (buttonRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const close = () => setOpen(false);
    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !buttonRef.current || !menuRef.current) return;
    const button = buttonRef.current.getBoundingClientRect();
    const menuHeight = menuRef.current.offsetHeight;
    const fitsBelow = button.bottom + GAP + menuHeight <= window.innerHeight;
    setPosition({
      top: fitsBelow ? button.bottom + GAP : Math.max(GAP, button.top - GAP - menuHeight),
      left: Math.max(GAP, button.right - MENU_WIDTH),
    });
  }, [open]);

  return (
    <div className="inline-block text-left">
      <button
        ref={buttonRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setPosition(null);
          setOpen((v) => !v);
        }}
        className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700"
      >
        <MoreVertical className="h-4 w-4" />
      </button>

      {open &&
        createPortal(
          <div
            ref={menuRef}
            style={{
              position: "fixed",
              width: MENU_WIDTH,
              top: position?.top ?? 0,
              left: position?.left ?? 0,
              visibility: position ? "visible" : "hidden",
            }}
            className="z-50 rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
          >
            {actions.map(({ key, label, icon: Icon, onClick, danger, disabled, disabledTitle }) => (
              <button
                key={key}
                type="button"
                disabled={disabled}
                title={disabled ? disabledTitle : undefined}
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(false);
                  onClick();
                }}
                className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors ${
                  disabled
                    ? "cursor-not-allowed text-gray-300"
                    : danger
                    ? "text-red-600 hover:bg-red-50"
                    : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                {Icon && <Icon className="h-4 w-4" />}
                {label}
              </button>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
};
